import React, { useEffect, useState } from "react";
import { adminFetch } from "../../utils/adminFetch";

// ─────────────────────────────────────────────────────────────
// Staffology Phase 2 — "Payroll Details" section for the employee
// edit drawer. Captures the UK payroll data Staffology needs but
// can't generate: statutory identity (NI number + category),
// starter declaration (+ optional P45), and pay (basis/amount/period).
//
// Loads + saves via the Phase-2 endpoints:
//   GET/PUT /api/payroll/uk-statutory/payroll-details/{user_id}
// These write LOCAL only — nothing is sent to Staffology (Phase 3).
//
// UK-only: the backend returns `is_uk` (region gate). India employees
// have their own fields, so we render a short note instead of the UK
// form for them.
// ─────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Match the drawer's theme.
const inputCls =
  "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls =
  "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const NI_CATEGORIES = ["A", "B", "C", "F", "H", "I", "J", "L", "M", "N", "S", "V", "X", "Z"];
const STARTER_OPTIONS = [
  { value: "A", label: "A — First job since 6 April (no other income)" },
  { value: "B", label: "B — This is now their only job" },
  { value: "C", label: "C — They have another job or a pension" },
];
const PAY_BASES = ["Monthly", "Weekly", "Daily", "Hourly"];
const PAY_PERIODS = ["Monthly", "Weekly", "Fortnightly", "FourWeekly", "Daily"];

// Basic client-side NI mirror of the server rule (2 letters, 6 digits,
// suffix A–D) — the server is authoritative; this just gives instant
// feedback. Spaces/case are ignored.
const NI_RE = /^[A-Z]{2}\d{6}[A-D]$/;
const niLooksValid = (v) => {
  if (!v) return true; // empty is allowed (not yet entered)
  return NI_RE.test(String(v).replace(/\s+/g, "").toUpperCase());
};

function TextField({ label, value, onChange, type = "text", placeholder, invalid, hint }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        className={`${inputCls} ${invalid ? "border-red-500/50 focus:border-red-500/70" : ""}`}
        value={value ?? ""}
        placeholder={placeholder || ""}
        type={type}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="text-[10px] text-white/30 mt-1">{hint}</p>}
      {invalid && <p className="text-[10px] text-red-400/70 mt-1">Invalid format</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder = "Select…" }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) =>
          typeof o === "string" ? (
            <option key={o} value={o}>{o}</option>
          ) : (
            <option key={o.value} value={o.value}>{o.label}</option>
          )
        )}
      </select>
    </div>
  );
}

const EMPTY = {
  ni_number: "",
  ni_category: "A",
  starter_declaration: "",
  p45_tax_code: "",
  p45_total_pay_to_date: "",
  p45_total_tax_to_date: "",
  p45_leaving_date: "",
  pay_basis: "",
  pay_amount: "",
  pay_period: "",
  payroll_region: "",   // "" = Auto (derive) | "UK" | "India"
};

export default function PayrollDetailsSection({ userId }) {
  const [meta, setMeta] = useState(null); // region / payroll_code / ctc hint / ready
  const [form, setForm] = useState(EMPTY);
  const [hasP45, setHasP45] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  };

  const load = () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    adminFetch(`${API_BASE}/api/payroll/uk-statutory/payroll-details/${userId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setMeta(d);
        setForm({
          ni_number: d.ni_number ?? "",
          ni_category: d.ni_category ?? "A",
          starter_declaration: d.starter_declaration ?? "",
          p45_tax_code: d.p45_tax_code ?? "",
          p45_total_pay_to_date: d.p45_total_pay_to_date ?? "",
          p45_total_tax_to_date: d.p45_total_tax_to_date ?? "",
          p45_leaving_date: d.p45_leaving_date ?? "",
          pay_basis: d.pay_basis ?? "",
          pay_amount: d.pay_amount ?? "",
          pay_period: d.pay_period ?? "",
          payroll_region: d.region_override ?? "",
        });
        setHasP45(
          Boolean(d.p45_tax_code || d.p45_total_pay_to_date || d.p45_total_tax_to_date || d.p45_leaving_date)
        );
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [userId]);

  const niInvalid = !niLooksValid(form.ni_number);

  const save = async () => {
    if (niInvalid) {
      setError("Please fix the NI number before saving.");
      return;
    }
    setSaving(true);
    setError(null);
    // Build a clean body: send null for blanks, numbers for numeric fields.
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const str = (v) => (v === "" || v === null || v === undefined ? null : v);
    const body = {
      ni_number: str(form.ni_number),
      ni_category: form.ni_category || "A",
      starter_declaration: str(form.starter_declaration),
      // When "Has P45" is off, explicitly clear the P45 fields.
      p45_tax_code: hasP45 ? str(form.p45_tax_code) : null,
      p45_total_pay_to_date: hasP45 ? num(form.p45_total_pay_to_date) : null,
      p45_total_tax_to_date: hasP45 ? num(form.p45_total_tax_to_date) : null,
      p45_leaving_date: hasP45 ? str(form.p45_leaving_date) : null,
      pay_basis: str(form.pay_basis),
      pay_amount: num(form.pay_amount),
      pay_period: str(form.pay_period),
      payroll_region: str(form.payroll_region),   // "" → null = derive
    };
    try {
      const res = await adminFetch(
        `${API_BASE}/api/payroll/uk-statutory/payroll-details/${userId}`,
        { method: "PUT", body: JSON.stringify(body) }
      );
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        } catch {}
        throw new Error(detail);
      }
      const updated = await res.json();
      setMeta(updated); // refresh readiness / hints
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Section header (always shown) ──
  const Header = (
    <div className="flex items-center justify-between mb-3 pb-1 border-b border-white/5">
      <p className="text-xs text-[#00ff90]/40 uppercase tracking-widest font-medium">
        Payroll Details
      </p>
      {meta && meta.is_uk && (
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full border ${
            meta.payroll_ready
              ? "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20"
              : "text-amber-400 bg-amber-500/10 border-amber-500/20"
          }`}
        >
          {meta.payroll_ready ? "✓ Payroll-ready" : "Incomplete"}
        </span>
      )}
    </div>
  );

  // Region control (Phase 4a) — always shown so an admin can correct a
  // mis-classified employee. Shows the derived region + how it was derived,
  // and lets the admin override it (Auto = derive from org/employee).
  const RegionControl = (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-white/30 uppercase tracking-widest">Payroll region</p>
        <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/60">
          {meta?.payroll_region || "unknown"}
          {meta?.region_source ? ` · ${meta.region_source.replace(/_/g, " ")}` : ""}
        </span>
      </div>
      <SelectField
        label="Region override (Auto = derive from org / country)"
        value={form.payroll_region}
        onChange={(v) => set("payroll_region", v)}
        options={["UK", "India"]}
        placeholder="Auto (derive)"
      />
    </div>
  );

  if (loading) {
    return (
      <div>
        {Header}
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-white/5 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // India employees: their UK NI/PAYE fields don't apply — but still show
  // the region control (so a mis-set India can be corrected) + a Save.
  if (meta && meta.payroll_region === "India") {
    return (
      <div>
        {Header}
        {RegionControl}
        <p className="text-xs text-white/40 leading-relaxed mb-3">
          This employee is on <span className="text-white/70">India</span> payroll — UK NI &amp; PAYE
          details don't apply here (India is handled by the local statutory engine). If this is wrong,
          set the region override above and save.
        </p>
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="text-xs">
            {error && <span className="text-red-400">⚠ {error}</span>}
            {saved && <span className="text-[#00ff90]">✓ Saved</span>}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium"
          >
            {saving ? "Saving…" : "Save Region"}
          </button>
        </div>
      </div>
    );
  }

  const gbp = (v) => (v || v === 0 ? `£${Number(v).toLocaleString()}` : "—");

  return (
    <div>
      {Header}
      {RegionControl}

      {/* Read-only context: payroll code + existing standing salary */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
          <p className="text-[10px] text-white/30 uppercase tracking-wider">Payroll Code</p>
          <p className="text-sm text-white/80 mt-0.5">{meta?.payroll_code || "— (set Employee ID)"}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
          <p className="text-[10px] text-white/30 uppercase tracking-wider">Current Annual CTC</p>
          <p className="text-sm text-white/80 mt-0.5">{gbp(meta?.existing_annual_ctc)}</p>
        </div>
      </div>

      {/* Statutory identity */}
      <p className="text-[10px] text-white/30 uppercase tracking-widest mb-2">Statutory identity</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <TextField
          label="NI Number"
          value={form.ni_number}
          onChange={(v) => set("ni_number", v)}
          placeholder="QQ123456C"
          invalid={niInvalid}
          hint="2 letters, 6 digits, suffix A–D"
        />
        <SelectField
          label="NI Category"
          value={form.ni_category}
          onChange={(v) => set("ni_category", v)}
          options={NI_CATEGORIES}
          placeholder="A"
        />
      </div>

      {/* Starter declaration */}
      <p className="text-[10px] text-white/30 uppercase tracking-widest mb-2">Starter declaration</p>
      <div className="grid grid-cols-1 gap-3 mb-2">
        <SelectField
          label="Declaration (sets initial tax code)"
          value={form.starter_declaration}
          onChange={(v) => set("starter_declaration", v)}
          options={STARTER_OPTIONS}
        />
      </div>

      {/* Has P45 toggle */}
      <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={hasP45}
          onChange={(e) => {
            setHasP45(e.target.checked);
            setSaved(false);
          }}
          className="accent-[#00ff90]"
        />
        <span className="text-xs text-white/60">This starter has a P45</span>
      </label>

      {hasP45 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 rounded-lg border border-white/5 bg-white/[0.02] p-3">
          <TextField
            label="P45 Tax Code"
            value={form.p45_tax_code}
            onChange={(v) => set("p45_tax_code", v)}
            placeholder="1257L"
          />
          <TextField
            label="P45 Leaving Date"
            value={form.p45_leaving_date}
            onChange={(v) => set("p45_leaving_date", v)}
            type="date"
          />
          <TextField
            label="Total Pay to Date"
            value={form.p45_total_pay_to_date}
            onChange={(v) => set("p45_total_pay_to_date", v)}
            type="number"
            placeholder="0.00"
          />
          <TextField
            label="Total Tax to Date"
            value={form.p45_total_tax_to_date}
            onChange={(v) => set("p45_total_tax_to_date", v)}
            type="number"
            placeholder="0.00"
          />
        </div>
      )}

      {/* Pay */}
      <p className="text-[10px] text-white/30 uppercase tracking-widest mb-2">Pay</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <SelectField
          label="Pay Basis"
          value={form.pay_basis}
          onChange={(v) => set("pay_basis", v)}
          options={PAY_BASES}
        />
        <TextField
          label="Pay Amount"
          value={form.pay_amount}
          onChange={(v) => set("pay_amount", v)}
          type="number"
          placeholder="0.00"
          hint="Annual salary, or the rate for the chosen basis"
        />
        <SelectField
          label="Pay Period"
          value={form.pay_period}
          onChange={(v) => set("pay_period", v)}
          options={PAY_PERIODS}
        />
      </div>

      {/* Region dependency note */}
      {meta && !meta.region_determined && (
        <p className="text-[10px] text-white/25 leading-relaxed mb-3">
          Note: this employee's region couldn't be determined from their record, so UK payroll
          fields are shown by default. Per-employee region routing arrives in a later phase.
        </p>
      )}

      {/* Save row */}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="text-xs">
          {error && <span className="text-red-400">⚠ {error}</span>}
          {saved && <span className="text-[#00ff90]">✓ Payroll details saved</span>}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium"
        >
          {saving ? "Saving…" : "Save Payroll Details"}
        </button>
      </div>
    </div>
  );
}
